<template>
  <div class="sc-tool">
    <div class="sc-input-row">
      <el-input v-model="domain" size="medium" :placeholder="$t('tool.SslChecker.phDomain')" class="sc-inp" @keyup.enter="query" />
      <el-button type="primary" size="medium" @click="query" :loading="loading">{{ $t('tool.SslChecker.btnQuery') }}</el-button>
    </div>

    <div class="sc-error" v-if="error">{{ error }}</div>

    <div class="sc-result" v-if="result">
      <!-- Summary cards -->
      <div class="sc-cards">
        <div class="sc-card" :class="result.expired ? 'sc-danger' : (result.expiringSoon ? 'sc-warn' : 'sc-ok')">
          <div class="sc-card-val">{{ result.expired ? $t('tool.SslChecker.expired') : (result.expiringSoon ? $t('tool.SslChecker.expiringSoon') : $t('tool.SslChecker.valid')) }}</div>
          <div class="sc-card-label">
            {{ $t('tool.SslChecker.remaining') }} <strong>{{ result.remainingDays }}</strong> {{ $t('tool.SslChecker.days') }}
            <div class="sc-card-sub">{{ $t('tool.SslChecker.to') }}{{ result.notAfter }}</div>
          </div>
        </div>
        <div class="sc-card">
          <div class="sc-card-val">{{ result.sigAlg }}</div>
          <div class="sc-card-label">{{ $t('tool.SslChecker.sigAlg') }}</div>
        </div>
        <div class="sc-card">
          <div class="sc-card-val">{{ result.publicKey.algorithm }} {{ result.publicKey.keySize }}bit</div>
          <div class="sc-card-label">{{ $t('tool.SslChecker.publicKey') }}</div>
        </div>
      </div>

      <!-- Details -->
      <div class="sc-section">
        <div class="sc-section-title">{{ $t('tool.SslChecker.certInfo') }}</div>
        <div class="sc-grid">
          <div class="sc-item"><span class="sc-item-lbl">{{ $t('tool.SslChecker.domainLabel') }}</span><span class="sc-item-val">{{ result.subject }}</span></div>
          <div class="sc-item"><span class="sc-item-lbl">{{ $t('tool.SslChecker.issuerLabel') }}</span><span class="sc-item-val">{{ result.issuer }} ({{ result.issuerOrg }})</span></div>
          <div class="sc-item"><span class="sc-item-lbl">{{ $t('tool.SslChecker.certType') }}</span><span class="sc-item-val">{{ result.certType }}</span></div>
          <div class="sc-item"><span class="sc-item-lbl">{{ $t('tool.SslChecker.validity') }}</span><span class="sc-item-val">{{ result.notBefore }} ~ {{ result.notAfter }}</span></div>
          <div class="sc-item"><span class="sc-item-lbl">{{ $t('tool.SslChecker.tlsProtocol') }}</span><span class="sc-item-val">{{ result.protocol }} / {{ result.cipherSuite }}</span></div>
          <div class="sc-item"><span class="sc-item-lbl">SHA-256</span><code class="sc-fp">{{ formatFp(result.sha256Fingerprint) }}</code></div>
        </div>
      </div>

      <!-- SANs -->
      <div class="sc-section" v-if="result.sans && result.sans.length">
        <div class="sc-section-title">{{ $t('tool.SslChecker.sanLabel') }}（{{ result.sans.length }}{{ $t('tool.SslChecker.sanCount') }}）</div>
        <div class="sc-sans">
          <span v-for="(san, i) in result.sans" :key="i" class="sc-san-tag">{{ san }}</span>
        </div>
      </div>

      <!-- Certificate chain -->
      <div class="sc-section" v-if="result.chain && result.chain.length">
        <div class="sc-section-title">{{ $t('tool.SslChecker.certChain') }}（{{ result.chain.length }} {{ $t('tool.SslChecker.chainLevel') }}）</div>
        <div class="sc-chain">
          <div v-for="(c, i) in result.chain" :key="i" class="sc-chain-item">
            <div class="sc-chain-icon">{{ c.type === 'leaf' ? '🔑' : (c.type === 'root' ? '🔒' : '📜') }}</div>
            <div class="sc-chain-body">
              <div class="sc-chain-subject">{{ c.subject }}</div>
              <div class="sc-chain-meta">{{ c.type === 'leaf' ? $t('tool.SslChecker.siteCert') : (c.type === 'root' ? $t('tool.SslChecker.rootCert') : $t('tool.SslChecker.intermediateCert')) }} · {{ $t('tool.SslChecker.issuerBy') }}：{{ c.issuer }}</div>
              <div class="sc-chain-exp" :class="{ 'sc-expired': c.expired === 'true' }">{{ $t('tool.SslChecker.expiryDate') }}：{{ c.notAfter }}</div>
            </div>
            <div v-if="i < result.chain.length - 1" class="sc-chain-arrow">↓</div>
          </div>
        </div>
      </div>
    </div>

    <div class="sc-empty" v-if="!loading && !result && !error">{{ $t('tool.SslChecker.emptyHint') }}</div>
  </div>
</template>

<script>
export default {
  name:'SslCheckerTool',
  props:{ config:{ type:Object, default:function(){ return {} } } },
  data:function(){
    return { domain:'', result:null, error:'', loading:false }
  },
  methods:{
    query:function(){
      var d = this.domain.trim()
      if (!d) { this.error = this.$t('tool.SslChecker.errDomainRequired'); return }
      this.loading = true; this.result = null; this.error = ''

      fetch('/api/pub/ssl/check?domain=' + encodeURIComponent(d))
        .then(function(r){ return r.json() })
        .then(function(res){
          this.loading = false
          var data = res.data
          if (data.error) { this.error = data.error; return }
          this.result = data
        }.bind(this))
        .catch(function(e){
          this.loading = false
          this.error = this.$t('tool.SslChecker.noResult') + ': ' + (e.message || this.$t('tool.SslChecker.errNetworkError'))
        }.bind(this))
    },
    formatFp:function(fp){
      if (!fp) return ''
      return fp.match(/.{1,2}/g).join(':')
    },
    doAction:function(a){
      if (a === 'clear') { this.domain = ''; this.result = null; this.error = '' }
    }
  }
}
</script>

<style scoped>
.sc-tool { width: 100%; }
.sc-input-row { display:flex;gap:10px;margin-bottom:20px; }
.sc-inp { flex:1; }
.sc-inp >>> input { font-family:monospace;font-size:14px !important; }

.sc-error { margin-bottom:16px;padding:12px 16px;background:#fef0f0;border-radius:8px;color:#f56c6c;font-size:13px; }

/* cards */
.sc-cards { display:flex;gap:12px;margin-bottom:16px; }
.sc-card { flex:1;background:#fff;border:1px solid #ebeef5;border-radius:10px;padding:14px;text-align:center; }
.sc-card.sc-ok { border-color:#c2e7b0;background:#f6fff0; }
.sc-card.sc-warn { border-color:#f5dab1;background:#fffbf0; }
.sc-card.sc-danger { border-color:#fbc4c4;background:#fff5f5; }
.sc-card-val { font-size:16px;font-weight:700;color:#303133; }
.sc-card.sc-ok .sc-card-val { color:#67c23a; }
.sc-card.sc-warn .sc-card-val { color:#e6a23c; }
.sc-card.sc-danger .sc-card-val { color:#f56c6c; }
.sc-card-label { font-size:12px;color:#909399;margin-top:4px; }
.sc-card-sub { font-size:11px;color:#c0c4cc;margin-top:2px; }

/* section */
.sc-section { background:#fff;border:1px solid #ebeef5;border-radius:10px;padding:16px 20px;margin-bottom:12px; }
.sc-section-title { font-size:13px;font-weight:700;color:#303133;margin-bottom:12px;padding-bottom:8px;border-bottom:1px solid #f5f6f8; }

.sc-grid { display:grid;grid-template-columns:1fr 1fr;gap:10px 20px; }
.sc-item { display:flex;flex-direction:column;gap:2px; }
.sc-item-lbl { font-size:11px;color:#909399;font-weight:600; }
.sc-item-val { font-size:13px;color:#303133;word-break:break-all; }
.sc-fp { font-size:11px;color:#4e7cff;font-family:monospace;word-break:break-all; }

.sc-sans { display:flex;flex-wrap:wrap;gap:6px; }
.sc-san-tag { display:inline-block;padding:3px 10px;background:#f0f4ff;color:#4e7cff;border-radius:4px;font-size:12px;font-family:monospace; }

/* chain */
.sc-chain { display:flex;flex-direction:column;align-items:center; }
.sc-chain-item { display:flex;align-items:flex-start;gap:10px;width:100%;padding:10px 0; }
.sc-chain-icon { font-size:20px;flex-shrink:0;width:32px;text-align:center; }
.sc-chain-body { flex:1; }
.sc-chain-subject { font-size:13px;font-weight:600;color:#303133; }
.sc-chain-meta { font-size:12px;color:#909399;margin-top:2px; }
.sc-chain-exp { font-size:11px;color:#67c23a;margin-top:2px; }
.sc-chain-exp.sc-expired { color:#f56c6c; }
.sc-chain-arrow { text-align:center;padding:0 16px;color:#c0c4cc;font-size:14px; }

.sc-empty { text-align:center;padding:40px;color:#c0c4cc;font-size:14px; }

@media (max-width:640px) {
  .sc-cards { flex-direction:column; }
  .sc-grid { grid-template-columns:1fr; }
}
</style>
